import { useQuery } from '@apollo/client';

import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import type {
  GetBestProgramToEnrollQuery,
  GetBestProgramToEnrollQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/GetBestProgramToEnroll';

import GET_BEST_PROGRAM_TO_ENROLL from '../queries/GetBestProgramToEnroll.graphql';

type BestProgramResult = {
  selectedProgramId: string;
  selectionReason: string;
};

type UseGetBestProgramToEnrollOptions = {
  productId: string;
  productType: string;
  organizationId: string;
  skip?: boolean;
};

type UseGetBestProgramToEnrollReturn = {
  bestProgram: BestProgramResult | null;
  loading: boolean;
  error: boolean;
};

/**
 * Fetches the best program for the authenticated user to enroll through for a given product.
 * The backend uses a cascading priority algorithm (membership, product availability,
 * academic integrity features, contract expiry) to determine the best program.
 *
 * When a result is returned, the enrollment flow can skip the program selection modal
 * and auto-enroll the learner into the selected program.
 */
export const useGetBestProgramToEnroll = ({
  productId,
  productType,
  organizationId,
  skip = false,
}: UseGetBestProgramToEnrollOptions): UseGetBestProgramToEnrollReturn => {
  const gqlProductType = productType === PRODUCT_TYPES.SPECIALIZATION ? 'SPECIALIZATION' : 'COURSE';

  const { data, loading, error } = useQuery<GetBestProgramToEnrollQuery, GetBestProgramToEnrollQueryVariables>(
    GET_BEST_PROGRAM_TO_ENROLL,
    {
      variables: {
        input: {
          productId,
          productType: gqlProductType,
          organizationId,
        },
      },
      context: { clientName: 'gatewayGql' },
      skip,
      fetchPolicy: 'network-only',
    }
  );

  const result = data?.EnterpriseEnrollment?.getBestProgramToEnroll;

  if (loading || skip) {
    return { bestProgram: null, loading: loading && !skip, error: false };
  }

  if (error || result?.__typename === 'EnterpriseEnrollment_GetBestProgramToEnrollFailure') {
    return { bestProgram: null, loading: false, error: true };
  }

  if (result?.__typename === 'EnterpriseEnrollment_GetBestProgramToEnrollSuccess') {
    return {
      bestProgram: {
        selectedProgramId: result.selectedProgramId,
        selectionReason: result.selectionReason,
      },
      loading: false,
      error: false,
    };
  }

  return { bestProgram: null, loading: false, error: false };
};
